import { AfterViewInit, Component, inject, Input, ChangeDetectionStrategy } from '@angular/core';
import { DetailsViewConfiguration } from '../../../../../shared/models/internal/details/details-view-configuration';
import { LinkedDtcsComponent } from '../linked-dtcs/linked-dtcs.component';
import { NgClass } from '@angular/common';
import { initFlowbite } from 'flowbite';
import { PreselectionService } from '../../../../services/preselection.service';
import { PreselectionType } from '../../../../../shared/enums/preselection-type';
import { CommonUtils } from '../../../../common/common-utils';
import { Textarea } from '../../../../../shared/models/internal/details/textarea';
import { SingleLineDetail } from '../../../../../shared/models/internal/details/single-line-detail';
import { ItemBadge } from '../../../../../shared/models/internal/details/item-badge';
import { LanguageIconComponent } from '../../../icons/language-icon/language-icon.component';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { AlarmMaskViewConfiguration } from '../../../../../shared/models/internal/details/alarm-mask-view-configuration';
import { AlarmMaskView } from '../../alarm-mask-view/alarm-mask-view';
import { Separator } from '../../../../../shared/models/internal/details/separator';
import { ApprovalStatus } from '../../../../../shared/models/internal/details/approval-status';
import { ApprovalStatusComponent } from '../approval-stats/approval-stats.comonent';
import { TrReviewStatistics } from '../../../../../shared/models/internal/details/tr-review-statistics';
import { EnabledByPolicyDirective } from '../../../../directives/enabled-by-policy.directive';
import { TrReviewStatusComponent } from '../tr-review-stats/tr-review-stats.comonent';

@Component({
  selector: 'app-entity-details-view',
  imports: [
    LinkedDtcsComponent,
    NgClass,
    LanguageIconComponent,
    AlarmMaskView,
    ApprovalStatusComponent,
    EnabledByPolicyDirective,
    TrReviewStatusComponent,
  ],
  templateUrl: './entity-details-view.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './entity-details-view.component.scss',
})
export class EntityDetailsViewComponent implements AfterViewInit {
  @Input() public detailsViewConfig?: DetailsViewConfiguration;
  public commonUtils = CommonUtils;

  private preselectionService = inject(PreselectionService);
  private appInsightsService = inject(AppInsightsService);

  public ngAfterViewInit(): void {
    initFlowbite();
    this.appInsightsService.logSidebarUsage('Summary');
  }

  public navigateWithId(id: string, searchTerm: string, itemType: PreselectionType): void {
    if (!id || !searchTerm) {
      return;
    }

    this.preselectionService.configureAndChangeRoute(id, searchTerm, itemType);
  }

  public getDetailsBySequenceOrder(): (
    | Textarea
    | SingleLineDetail
    | ItemBadge
    | AlarmMaskViewConfiguration
    | Separator
    | ApprovalStatus
    | TrReviewStatistics
  )[] {
    if (!this.detailsViewConfig) {
      return [];
    }
    const singleLines = this.detailsViewConfig.singleLineDetails ?? [];
    const textAreas = this.detailsViewConfig.textAreas ?? [];
    const itemBadges = this.detailsViewConfig.badge ? [this.detailsViewConfig.badge] : [];
    const alarmMaskViews = this.detailsViewConfig.alarmMaskView ? [this.detailsViewConfig.alarmMaskView] : [];
    const approvalStats = this.detailsViewConfig.approvalStats ? this.detailsViewConfig.approvalStats : [];
    const trReviewStats = this.detailsViewConfig.trReviewStats ? this.detailsViewConfig.trReviewStats : [];
    const separators = this.detailsViewConfig.separators
      ? this.detailsViewConfig.separators.map((seq) => {
          return {
            sequence: seq,
            separatorId: seq.toString(),
          } as Separator;
        })
      : [];
    return [
      ...singleLines,
      ...textAreas,
      ...itemBadges,
      ...alarmMaskViews,
      ...separators,
      ...approvalStats,
      ...trReviewStats,
    ].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  }

  public isTextArea(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is Textarea {
    return (item as Textarea).label !== undefined;
  }

  public isSingleLineDetail(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is SingleLineDetail {
    return (item as SingleLineDetail).attributes !== undefined;
  }

  public isItemBadge(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is ItemBadge {
    return (item as ItemBadge).title !== undefined;
  }

  public isAlarmMaskView(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is AlarmMaskViewConfiguration {
    return (item as AlarmMaskViewConfiguration).alarmMasks !== undefined;
  }

  public isSeparator(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is Separator {
    return (item as Separator).separatorId !== undefined;
  }

  public isApprovalStatus(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is ApprovalStatus {
    return (item as ApprovalStatus).approvalStatus !== undefined;
  }

  public isTrReviewStatistics(
    item:
      | Textarea
      | SingleLineDetail
      | ItemBadge
      | AlarmMaskViewConfiguration
      | Separator
      | ApprovalStatus
      | TrReviewStatistics
  ): item is TrReviewStatistics {
    return (item as TrReviewStatistics).trReviewStatistics !== undefined;
  }
}
